import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetUserTeamsQuery } from "../../features/members/membersApi";
import { teamsApi } from "../../features/teams/teamsApi";
import {
    projectsApi,
    useAddProjectMutation,
} from "../../features/projects/projectsApi";
import ProjectItem from "./ProjectItem";
import ProjectColumn from "./ProjectColumn";
import Modal from "../ui/Modal";
import Error from "../ui/Error";
import debounceHandler from "../../utils/debounceHandler";
import makeProjectName from "../../utils/makeProjectName";

const states = [
    {
        name: "Backlog",
        id: 1,
    },
    {
        name: "Ready",
        id: 2,
    },
    {
        name: "Doing",
        id: 3,
    },
    {
        name: "Review",
        id: 4,
    },
    {
        name: "Blocked",
        id: 5,
    },
    {
        name: "Done",
        id: 6,
    },
];

export default function Projects() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth) || {};
    const { email } = user || {};
    const { title: search } = useSelector((state) => state.filters) || {};

    const [addProject, { isSuccess: isAddProjectSuccess, data: projectData }] =
        useAddProjectMutation();

    // Get member list of team for user
    const { data: userTeams } = useGetUserTeamsQuery(email) || {};

    const [teams, setTeams] = useState([]);

    // Get user all teams
    useEffect(() => {
        if (userTeams?.length > 0) {
            const allTeams = userTeams?.map((tId) => tId.teamId);

            dispatch(teamsApi.endpoints.getTeams.initiate(allTeams))
                .unwrap()
                .then((data) => {
                    setTeams(data);
                })
                .catch((err) => {});
        }
    }, [userTeams, dispatch]);

    const [projects, setProjects] = useState([]);
    const [projectModal, setProjectModal] = useState(false);
    const [name, setName] = useState("");
    const [teamId, setTeamId] = useState("");
    const [teamData, setTeamData] = useState({});

    const [error, setError] = useState("");
    const [nameError, setNameError] = useState("");

    // Get all projects
    useEffect(() => {
        if (userTeams?.length > 0) {
            const allTeams = userTeams?.map((tId) => tId.teamId);

            dispatch(projectsApi.endpoints.getProjects.initiate(allTeams))
                .unwrap()
                .then((data) => {
                    setProjects(data);
                })
                .catch((err) => {});
        }
    }, [userTeams, dispatch]);

    const projectCount = (state) => {
        return projects.filter((p) => p.stateId === state).length;
    };

    const projectFilter = (state) => {
        const filterData = projects.filter((p) => p.stateId === state);
        return projectTitleFilter(filterData);
    };

    const projectTitleFilter = (projects) => {
        if (search) {
            const data = projects?.map((p) => {
                const filterData = p.title
                    .toLowerCase()
                    .includes(search.toLowerCase());
                if (filterData) {
                    return { ...p, filterSelect: true };
                }
                return { ...p };
            });

            return data;
        } else {
            return projects;
        }
    };

    const projectStateChange = (id, stateId) => {
        const changeProject = projects.map((p) => {
            if (p.id !== id) return { ...p };
            return { ...p, stateId: stateId };
        });
        setProjects(changeProject);
    };

    useEffect(() => {
        setName("");
        setTeamData({});
        setTeamId("");
        setError("");
        setNameError("");
    }, [isAddProjectSuccess]);

    useEffect(() => {
        if (projectData) {
            const newData = [...projects, { ...projectData }];
            setProjects(newData);
        }
    }, [projectData]);

    const projectDelete = (id) => {
        const newData = projects.filter((p) => p.id !== id);
        setProjects(newData);
    };

    const doSearch = (value) => {
        setNameError("");
        // check project name in database
        setName(value);

        const nameString = makeProjectName(value);

        dispatch(projectsApi.endpoints.getProject.initiate(nameString))
            .unwrap()
            .then((data) => {
                if (data.length > 0) {
                    setNameError("Project name is already exists");
                }
            })
            .catch((err) => {});
    };

    const handleSearch = debounceHandler(doSearch, 500);

    const projectModalOpen = () => setProjectModal(true);
    const projectModalClose = () => setProjectModal(false);

    useEffect(() => {
        if (teamId) {
            const team = teams.find((t) => t.id == teamId);

            setTeamData({
                teamId: team.id,
                name: team.name,
                color: team.color,
            });
        }
    }, [teamId, teams]);

    const handleSubmit = (e) => {
        e.preventDefault();

        setError("");
        setNameError("");

        if (!name) {
            setError("Project name is required!!");
            return;
        }
        if (!teamId) {
            setError("Select team is required!!");
            return;
        }

        const data = {
            title: makeProjectName(name),
            team: teamData,
            creator: {
                email: user.email,
                userId: user.id,
            },
            stateId: 1,
            timestamp: new Date().getTime(),
        };
        const allTeams = userTeams?.map((tId) => tId.teamId);

        if (!nameError && name && teamId && !error)
            addProject({ data, team: allTeams });
        projectModalClose();
    };

    return (
        <>
            <div className="flex flex-grow px-10 mt-4 space-x-6 overflow-auto">
                {states?.map((state) => (
                    <div
                        key={state.id}
                        className="flex flex-col flex-shrink-0 w-72"
                    >
                        <div className="flex items-center flex-shrink-0 h-10 px-2">
                            <span className="block text-sm font-semibold">
                                {state.name}
                            </span>
                            <span className="flex items-center justify-center w-5 h-5 ml-2 text-sm font-semibold text-indigo-500 bg-white rounded bg-opacity-30">
                                {projectCount(state.id)}
                            </span>
                            {state.id === 1 && (
                                <button
                                    onClick={projectModalOpen}
                                    className="flex items-center justify-center w-6 h-6 ml-auto text-indigo-500 rounded hover:bg-indigo-500 hover:text-indigo-100"
                                >
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                        />
                                    </svg>
                                </button>
                            )}
                        </div>
                        <ProjectColumn
                            stateId={state.id}
                            projectStateChange={projectStateChange}
                        >
                            {projectFilter(state.id)?.map((project) => (
                                <ProjectItem
                                    key={project.id}
                                    state={state}
                                    project={project}
                                    projectDelete={projectDelete}
                                />
                            ))}
                        </ProjectColumn>
                    </div>
                ))}
                <div className="flex-shrink-0 w-6" />
            </div>

            {projectModal && (
                <Modal
                    open={projectModal}
                    close={projectModalClose}
                    title="Add Project"
                >
                    <form className="space-y-3" onSubmit={handleSubmit}>
                        {error && <Error message={error} />}
                        <div>
                            <label
                                htmlFor="name"
                                className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-300"
                            >
                                Name
                            </label>
                            <input
                                type="text"
                                id="name"
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-200 focus:border-blue-300 block w-full p-1.5"
                                placeholder="Name"
                                // value={name}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                            {nameError && (
                                <span className="text-red-600">
                                    {nameError}
                                </span>
                            )}
                        </div>
                        <div>
                            <label
                                htmlFor="team"
                                className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-300"
                            >
                                Select team
                            </label>
                            <select
                                onChange={(e) => setTeamId(e.target.value)}
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-200 focus:border-blue-300 block w-full p-1.5"
                            >
                                <option value="">Select a team</option>
                                {teams.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            type="submit"
                            className="w-full text-white bg-slate-700 hover:bg-slate-800 focus:ring-2 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center "
                        >
                            Save
                        </button>
                    </form>
                </Modal>
            )}
        </>
    );
}
